package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.*
import com.dotdrives.repository.WorkflowRepository
import kotlinx.coroutines.*
import kotlinx.serialization.json.*

data class StatementDocumentState(
    val id: String? = null,
    val document: DocumentDownload? = null,
    val loading: Boolean = false,
    val error: String? = null,
)

class StatementDocumentController(private val repo: WorkflowRepository, scope: CoroutineScope) :
    DotController<StatementDocumentState>(StatementDocumentState(), scope) {
    private var generation = 0
    private var job: Job? = null

    fun open(id: String, retry: Boolean = false) {
        val gen = ++generation
        job?.cancel()
        update { StatementDocumentState(id = id, loading = true) }
        job =
            scope.launch {
                try {
                    if (retry) repo.execute(request(id, "statement.retry")).getOrThrow()
                    repeat(12) { attempt ->
                        val raw = repo.execute(request(id, "statement.download")).getOrThrow()
                        if (gen != generation) return@launch
                        val doc =
                            Json { ignoreUnknownKeys = true }
                                .decodeFromJsonElement<DocumentDownload>(raw)
                        update { StatementDocumentState(id, doc, false) }
                        if (doc.status !in listOf("queued", "processing")) return@launch
                        if (attempt < 11) delay(5000)
                    }
                } catch (e: Throwable) {
                    if (e is CancellationException) throw e
                    if (gen == generation)
                        update {
                            it.copy(
                                loading = false,
                                error = e.workflowMessage("Unable to load the document. Retry."),
                            )
                        }
                }
            }
    }

    private fun request(id: String, action: String) =
        WorkflowRequest(
            "workflows",
            buildJsonObject {
                put("action", action)
                put("statement_id", id)
            },
        )
}
